import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { runStepOptimistically } from "../lib/runStepOptimistically";
import { routes, useCurrentUrl } from "../lib/urls";
import { ClickButton } from "./ClickButton";
import { Icon } from "./Icon";
import { Tooltip } from "./Tooltip";
import type { StepData } from "./Workflow";

const RunButton = styled(ClickButton)`
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const Container = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  position: relative;
  overflow: hidden;

  .shape {
    position: absolute;
    inset: 0;
    background: var(--200);
    z-index: 0;
    transition: all 75ms ease-in-out;
    border-radius: 9999px;
  }

  .line {
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 0.5px;
    transition: opacity 75ms ease-in-out;
    background: var(--400);
    opacity: 0.8;
  }

  &:hover,
  &:has(button:active) {
    .line {
      opacity: 0;
    }
  }

  &:has(button:active) {
    .shape {
      background: var(--400);
    }
  }

  &.single:hover {
    .shape {
      background: var(--400);
    }
  }

  &:not(.single):has(button:first-of-type:hover),
  &:not(.single):has(button:first-of-type:active) {
    .shape {
      right: 50%;
    }
  }

  &:not(.single):has(button:last-of-type:hover),
  &:not(.single):has(button:last-of-type:active) {
    .shape {
      left: 50%;
    }
  }
`;

type Props = {
  step: StepData;
  commitId: string;
  branchId: string;
  onExecution: (id: string) => void;
  disabled?: boolean;
  disabledReason?: string;
};

export function PlayButtons({
  step,
  commitId,
  branchId,
  onExecution,
  disabled,
  disabledReason,
}: Props) {
  const z = useZero();
  const { workflowId } = useCurrentUrl(routes.space.workflow);
  const [secondHovered, setSecondHovered] = useState(false);
  const hasDownstream = (step.links ?? []).length > 0;
  const isDisabled = disabled || disabledReason != null;
  const stepColor = disabledReason ? "gray" : (step?.color ?? "gray");
  const iconColor = disabledReason
    ? "var(--text-secondary)"
    : `var(--${stepColor}-600)`;
  const soloTip = disabledReason ?? "Solo run";
  const runAllTip = disabledReason ?? " Run all ";

  const handleRun = ({ ephemeral }: { ephemeral: boolean }) => {
    if (isDisabled) return;
    const executionId = runStepOptimistically(z, {
      workflowId,
      branchId,
      commitId,
      stepDir: step.name,
      ephemeral,
    });
    onExecution(executionId);
  };

  return (
    <Container
      className={hasDownstream ? undefined : "single"}
      style={
        {
          "--200": `var(--${stepColor}-200)`,
          "--400": `var(--${stepColor}-400)`,
          ...(isDisabled ? { opacity: 0.5, cursor: "default" } : {}),
        } as React.CSSProperties
      }
    >
      <div className="shape" />
      {hasDownstream && <div className="line" />}
      {hasDownstream ? (
        <Tooltip content={secondHovered ? runAllTip : soloTip}>
          <RunButton
            disabled={isDisabled}
            onClick={() => handleRun({ ephemeral: true })}
          >
            <Icon name="solo-run" size={12} color={iconColor} />
          </RunButton>
          <RunButton
            disabled={isDisabled}
            onClick={() => handleRun({ ephemeral: false })}
            onMouseEnter={() => setSecondHovered(true)}
            onMouseLeave={() => setSecondHovered(false)}
          >
            <Icon name="run" size={12} color={iconColor} />
          </RunButton>
        </Tooltip>
      ) : (
        <Tooltip content={soloTip}>
          <RunButton
            disabled={isDisabled}
            onClick={() => handleRun({ ephemeral: true })}
          >
            <Icon name="solo-run" size={12} color={iconColor} />
          </RunButton>
        </Tooltip>
      )}
    </Container>
  );
}
